import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileText, 
  faChevronDown,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';

interface ScriptData {
  id: string;
  title?: string;
  generatedScript: string;
  userSelections: {
    userIdea: string;
  };
  scriptMetadata?: {
    scriptType?: string;
  };
  createdAt: string;
}

interface ScriptDropdownProps {
  scripts: ScriptData[];
  selectedScriptId: string;
  onScriptSelect: (scriptId: string) => void;
  loading: boolean;
}

const ScriptDropdown: React.FC<ScriptDropdownProps> = ({
  scripts,
  selectedScriptId,
  onScriptSelect,
  loading
}) => {
  const { t } = useTranslation('scriptGenerator');
  
  const generateTitle = (script: ScriptData): string => {
    if (script.title) return script.title;
    
    const userIdea = script.userSelections?.userIdea || '';
    const words = userIdea.trim().split(/\s+/);
    const title = words.slice(0, 6).join(' ');
    
    return title.length > 40 
      ? title.substring(0, 37) + '...' 
      : title || t('scriptGenerator.scriptLayout.untitledScript');
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        color: '#a0a0a0'
      }}>
        <FontAwesomeIcon icon={faSpinner} spin />
        <span style={{ marginLeft: '8px' }}>{t('scriptGenerator.scriptLayout.loading')}</span>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'block',
        marginBottom: '8px',
        color: '#e0e0e0',
        fontSize: '14px',
        fontWeight: '500'
      }}>
        {t('scriptGenerator.scriptToPromptModal.selectScript')}
      </label>
      
      <div style={{ position: 'relative' }}>
        <select
          value={selectedScriptId}
          onChange={(e) => onScriptSelect(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 40px 12px 16px',
            backgroundColor: '#333',
            border: '1px solid #555',
            borderRadius: '6px',
            color: '#e0e0e0',
            fontSize: '14px',
            appearance: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#3b82f6';
            e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#555';
            e.target.style.boxShadow = 'none';
          }}
        >
          <option value="">{t('scriptGenerator.scriptToPromptModal.chooseScript')}</option>
          {scripts.map((script) => (
            <option key={script.id} value={script.id}>
              {generateTitle(script)} - {formatDate(script.createdAt)}
            </option>
          ))}
        </select>
        
        <FontAwesomeIcon 
          icon={faChevronDown} 
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#888',
            pointerEvents: 'none'
          }}
        />
      </div>
    </div>
  );
};

export default ScriptDropdown;